import { Button } from 'primereact/button';

import { getShowConfirmationDialog } from '../../../../contexts/utils/confirmDialogGlobal';
import { EmptyFallback } from '../WithLazyLoad';
import { Colors } from '../../../../constants';
import { hasAppliedThemeClass } from '../../Topbar/helpers/header.helper';

export function retryLazyImport<T>(
  importFunc: () => Promise<T>,
  maxRetries = 1,
  delay = 1000
): () => Promise<T> {
  return () => {
    let retries = 0;

    const attempt = async (): Promise<T> => {
      try {
        return await importFunc();
      } catch (error) {
        const isChunkLoadError = (() => {
          if (error instanceof Error) {
            return (
              error.name === 'ChunkLoadError' ||
              /Loading chunk [\d]+ failed/.test(error.message) ||
              /Loading CSS chunk [\d]+ failed/.test(error.message) ||
              error.message.includes('Loading chunk') ||
              error.message.includes('ChunkLoadError')
            );
          }
          return false;
        })();

        if (isChunkLoadError && retries < maxRetries) {
          retries++;

          console.warn(`Error loading, retrying...`);

          return new Promise<T>(resolve => {
            setTimeout(() => resolve(attempt()), delay);
          });
        }

        if (isChunkLoadError) {
          const showDialog = getShowConfirmationDialog();
          if (showDialog) {
            showDialog({
              message: (
                <div className="flex align- items-start gap-2">
                  <i
                    className="pi pi-exclamation-circle"
                    style={{
                      color: Colors.PF_BLUE,
                      fontSize: '1rem',
                      marginTop: '0.2rem', // Align icon with first line
                    }}
                  ></i>
                  <div>
                    <p style={{ margin: 0 }}>
                      An updated version of the application is available.
                    </p>
                    <p style={{ margin: 0 }}>
                      Please refresh to continue with the latest features.
                    </p>
                  </div>
                </div>
              ),
              header: 'Update Required',
              pt: {
                header: {
                  className: 'border-bottom-1 border-400',
                  style: {
                    backgroundColor: '#eff6ff',
                    padding: '1rem 1rem',
                  },
                },
                content: {
                  style: {
                    paddingTop: '1.5rem',
                    paddingLeft: '0.75rem',
                    paddingBottom: '1.5rem',
                    paddingRight: '1.5rem',
                  },
                },
                footer: {
                  className: 'border-top-1 border-400',
                  style: {
                    padding: '0.75rem 0.75rem',
                    display: 'flex',
                    justifyContent: 'flex-end',
                  },
                },
              },
              position: 'top',
              closable: false,
              closeOnEscape: false,
              blockScroll: true,
              reject: undefined,

              footer: (
                <Button
                  label="Reload Now"
                  icon="pi pi-refresh"
                  className={`${!hasAppliedThemeClass() && 'default-blue-bg default-blue-border'}`}
                  onClick={() => window.location.reload()}
                  autoFocus
                  size="small"
                />
              ),
            });
          }

          return Promise.resolve({ default: EmptyFallback } as unknown as T);
        }

        throw error;
      }
    };

    return attempt();
  };
}
